// src/features/template/components/BiasesPanel.tsx
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Tooltip,
  CircularProgress
} from '@mui/material';
import { 
  fetchBiases,
  selectSenderBiases,
  selectContentBiases,
  selectBiasesLoading,
  selectBiasesError
} from '../../../store/slices/biasesSlice';
import { AppDispatch } from '../../../store/store';
import { BiasOption } from '../types/index';

interface Props {
  senderBias: string;
  contentBiases: string[];
  onSenderBiasChange: (bias: string) => void;
  onContentBiasChange: (biases: string[]) => void;
  errors?: {
    senderBias?: string;
    contentBiases?: string;
  };
}

const commonButtonStyles = {
  justifyContent: 'flex-start',
  px: 2,
  py: 1.5,
  height: '44px',
  minHeight: '44px',
  borderRadius: '8px',
  borderColor: '#1B2B3A',
  color: 'white',
  textTransform: 'none',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

const tooltipStyles = {
  tooltip: {
    backgroundColor: '#1B2B3A',
    color: 'white',
    fontSize: '0.75rem', // Reduced from 0.875rem
    padding: '6px 8px', // Reduced from 8px 12px
    maxWidth: 180, // Reduced from 220
    borderRadius: '4px',
    boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.1)'
  },
  arrow: {
    color: '#1B2B3A'
  }
};

export const BiasesPanel: React.FC<Props> = ({
  senderBias,
  contentBiases,
  onSenderBiasChange,
  onContentBiasChange,
  errors
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const availableSenderBiases = useSelector(selectSenderBiases);
  const availableContentBiases = useSelector(selectContentBiases);
  const loading = useSelector(selectBiasesLoading);
  const error = useSelector(selectBiasesError);

  useEffect(() => {
    console.log('Fetching biases...');
    dispatch(fetchBiases());
  }, [dispatch]);

  console.log('BiasesPanel state:', {
    availableSenderBiases,
    availableContentBiases,
    loading,
    error
  });

  if (loading) {
    return (
      <Paper
        elevation={0}
        sx={{ 
          width: 300, 
          backgroundColor: '#243B53',
          p: 3,
          borderRadius: '12px',
          m: 2,
          color: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: 200
        }}
      >
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{ 
          width: 300, 
          backgroundColor: '#243B53',
          p: 3,
          borderRadius: '12px',
          m: 2,
          color: 'white'
        }}
      >
        <Typography color="error">
          Error loading biases: {error}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{ 
        width: 300, 
        backgroundColor: '#243B53',
        p: 3,
        borderRadius: '12px',
        m: 2,
        color: 'white',
        position: 'sticky',
        top: 24
      }}
    >
      <Typography variant="h6" sx={{ mb: 3, fontWeight: 500 }}>
        Biases
      </Typography>
      
      <Box sx={{ mb: 4 }}>
        <Typography sx={{ mb: 2, fontWeight: 500 }}>
          Sender Bias
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {availableSenderBiases.map((bias: BiasOption) => (
            <Tooltip
              key={bias.name}
              title={bias.description}
              arrow
              placement="left"
              componentsProps={{
                tooltip: { sx: tooltipStyles.tooltip },
                arrow: { sx: tooltipStyles.arrow }
              }}
              enterDelay={400}
              enterNextDelay={100}
            >
              <Button
                variant={senderBias === bias.name ? "contained" : "outlined"}
                onClick={() => onSenderBiasChange(bias.name)}
                sx={{
                  ...commonButtonStyles,
                  backgroundColor: senderBias === bias.name ? '#1B2B3A' : 'transparent',
                  borderColor: errors?.senderBias ? 'rgba(255, 82, 82, 0.87)' : '#1B2B3A',
                  '&:hover': {
                    backgroundColor: senderBias === bias.name ? '#1B2B3A' : 'rgba(27, 43, 58, 0.5)',
                    borderColor: errors?.senderBias ? 'rgba(255, 82, 82, 0.87)' : '#1B2B3A'
                  }
                }}
              >
                {bias.name}
              </Button>
            </Tooltip>
          ))}
        </Box>
        {errors?.senderBias && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 82, 82, 0.87)',
              mt: 1,
              display: 'block'
            }}
          >
            {errors.senderBias}
          </Typography>
        )}
      </Box>

      <Box>
        <Typography sx={{ mb: 2, fontWeight: 500 }}>
          Content Biases
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {availableContentBiases.map((bias: BiasOption) => (
            <Tooltip
              key={bias.name}
              title={bias.description}
              arrow
              placement="left"
              componentsProps={{
                tooltip: { sx: tooltipStyles.tooltip },
                arrow: { sx: tooltipStyles.arrow }
              }}
              enterDelay={400}
              enterNextDelay={100}
            >
              <Button
                variant={contentBiases.includes(bias.name) ? "contained" : "outlined"}
                onClick={() => {
                  if (contentBiases.includes(bias.name)) {
                    onContentBiasChange(contentBiases.filter(b => b !== bias.name));
                  } else {
                    onContentBiasChange([...contentBiases, bias.name]);
                  }
                }}
                sx={{
                  ...commonButtonStyles,
                  backgroundColor: contentBiases.includes(bias.name) ? '#1B2B3A' : 'transparent',
                  borderColor: errors?.contentBiases ? 'rgba(255, 82, 82, 0.87)' : '#1B2B3A',
                  '&:hover': {
                    backgroundColor: contentBiases.includes(bias.name) ? '#1B2B3A' : 'rgba(27, 43, 58, 0.5)',
                    borderColor: errors?.contentBiases ? 'rgba(255, 82, 82, 0.87)' : '#1B2B3A'
                  }
                }}
              >
                {bias.name}
              </Button>
            </Tooltip>
          ))}
        </Box>
        {errors?.contentBiases && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: 'rgba(255, 82, 82, 0.87)',
              mt: 1,
              display: 'block'
            }}
          >
            {errors.contentBiases}
          </Typography>
        )}
      </Box>
    </Paper>
  );
};
